/**
 * Project Save/Load Utilities for Mass Master Pro
 * Handles saving and loading all user data including custom entries
 */

const STORAGE_KEYS = {
  PROJECT_DATA: 'massMasterPro_projectData',
  CUSTOM_MATERIALS: 'massMasterPro_customMaterials',
  CUSTOM_TRUCKS: 'massMasterPro_customTrucks',
  CUSTOM_PIPES: 'massMasterPro_customPipes',
  CUSTOM_TANKS: 'massMasterPro_customTanks',
  SAVED_PROJECTS: 'massMasterPro_savedProjects',
  SETTINGS: 'massMasterPro_settings',
  LAST_SAVE: 'massMasterPro_lastSave'
};

/**
 * Get all current project data
 */
export const getCurrentProjectData = ({
  projectInfo,
  companyInfo,
  areaMaterials,
  bulkMaterials,
  excavationMaterials,
  pipingMaterials,
  tankMaterials,
  buildings,
  truckingCalculations,
  customImportVolumes,
  customExcavationVolumes,
  materialItems,
  laborItems,
  equipmentItems,
  additionalCosts,
  settings,
  lineItemMarkups
}) => {
  return {
    version: '1.0',
    savedAt: new Date().toISOString(),
    projectInfo,
    companyInfo,
    calculators: {
      areaMaterials,
      bulkMaterials,
      excavationMaterials,
      pipingMaterials,
      tankMaterials,
      buildings,
      truckingCalculations,
      customImportVolumes,
      customExcavationVolumes
    },
    estimate: {
      materialItems,
      laborItems,
      equipmentItems,
      additionalCosts,
      settings,
      lineItemMarkups
    },
    customEntries: {
      materials: JSON.parse(localStorage.getItem('customMaterialTypes') || '[]'),
      bulkMaterials: JSON.parse(localStorage.getItem('customBulkMaterials') || '[]'),
      excavationMaterials: JSON.parse(localStorage.getItem('customExcavationMaterials') || '[]'),
      trucks: JSON.parse(localStorage.getItem('customTruckTypes') || '[]'),
      pipes: JSON.parse(localStorage.getItem('customPipeTypes') || '[]'),
      tanks: JSON.parse(localStorage.getItem('customTankTypes') || '[]'),
      cyMaterials: JSON.parse(localStorage.getItem('customCyToTonsMaterials') || '[]'),
      materialTypesList: JSON.parse(localStorage.getItem('estimateMaterialTypes') || '[]'),
      laborTypesList: JSON.parse(localStorage.getItem('estimateLaborTypes') || '[]'),
      equipmentTypesList: JSON.parse(localStorage.getItem('estimateEquipmentTypes') || '[]')
    }
  };
};

/**
 * Save current project to localStorage
 */
export const saveProjectToStorage = (projectData, projectName = null) => {
  try {
    const name = projectName || projectData.projectInfo?.name || `Project_${Date.now()}`;
    const saveData = {
      ...projectData,
      projectName: name,
      savedAt: new Date().toISOString()
    };

    // Save as current project
    localStorage.setItem(STORAGE_KEYS.PROJECT_DATA, JSON.stringify(saveData));
    localStorage.setItem(STORAGE_KEYS.LAST_SAVE, new Date().toISOString());

    // Also add to saved projects list
    const savedProjects = JSON.parse(localStorage.getItem(STORAGE_KEYS.SAVED_PROJECTS) || '[]');
    const existingIndex = savedProjects.findIndex(p => p.projectName === name);
    
    if (existingIndex >= 0) {
      savedProjects[existingIndex] = saveData;
    } else {
      savedProjects.unshift(saveData);
    }

    // Keep only last 20 projects
    const trimmedProjects = savedProjects.slice(0, 20);
    localStorage.setItem(STORAGE_KEYS.SAVED_PROJECTS, JSON.stringify(trimmedProjects));

    return { success: true, message: `Project "${name}" saved successfully`, savedAt: saveData.savedAt };
  } catch (error) {
    console.error('Error saving project:', error);
    return { success: false, message: 'Failed to save project: ' + error.message };
  }
};

/**
 * Load project from localStorage
 */
export const loadProjectFromStorage = (projectName = null) => {
  try {
    if (projectName) {
      const savedProjects = JSON.parse(localStorage.getItem(STORAGE_KEYS.SAVED_PROJECTS) || '[]');
      const project = savedProjects.find(p => p.projectName === projectName);
      if (project) return { success: true, data: project };
      return { success: false, message: 'Project not found' };
    }

    const data = localStorage.getItem(STORAGE_KEYS.PROJECT_DATA);
    if (data) {
      return { success: true, data: JSON.parse(data) };
    }
    return { success: false, message: 'No saved project found' };
  } catch (error) {
    console.error('Error loading project:', error);
    return { success: false, message: 'Failed to load project: ' + error.message };
  }
};

/**
 * Get list of saved projects
 */
export const getSavedProjectsList = () => {
  try {
    const savedProjects = JSON.parse(localStorage.getItem(STORAGE_KEYS.SAVED_PROJECTS) || '[]');
    return savedProjects.map(p => ({
      name: p.projectName,
      savedAt: p.savedAt,
      projectInfo: p.projectInfo
    }));
  } catch (error) {
    console.error('Error getting saved projects:', error);
    return [];
  }
};

/**
 * Delete a saved project
 */
export const deleteSavedProject = (projectName) => {
  try {
    const savedProjects = JSON.parse(localStorage.getItem(STORAGE_KEYS.SAVED_PROJECTS) || '[]');
    const filtered = savedProjects.filter(p => p.projectName !== projectName);
    localStorage.setItem(STORAGE_KEYS.SAVED_PROJECTS, JSON.stringify(filtered));
    return { success: true, message: 'Project deleted' };
  } catch (error) {
    console.error('Error deleting project:', error);
    return { success: false, message: 'Failed to delete project' };
  }
};

/**
 * Export project to JSON file for download
 */
export const exportProjectToFile = (projectData) => {
  try {
    const fileName = `${projectData.projectInfo?.name || 'MassMasterPro_Project'}_${new Date().toISOString().split('T')[0]}.json`;
    const blob = new Blob([JSON.stringify(projectData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true, message: 'Project exported successfully' };
  } catch (error) {
    console.error('Error exporting project:', error);
    return { success: false, message: 'Failed to export project' };
  }
};

/**
 * Import project from JSON file
 */
export const importProjectFromFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.version && data.projectInfo) {
          resolve({ success: true, data });
        } else {
          resolve({ success: false, message: 'Invalid project file format' });
        }
      } catch (error) {
        resolve({ success: false, message: 'Failed to parse project file' });
      }
    };
    reader.onerror = () => {
      resolve({ success: false, message: 'Failed to read file' });
    };
    reader.readAsText(file);
  });
};

/**
 * Apply loaded project data to state setters
 */
export const applyProjectData = (data, setters) => {
  const {
    setProjectInfo,
    setCompanyInfo,
    setAreaMaterials,
    setBulkMaterials,
    setExcavationMaterials,
    setPipingMaterials,
    setTankMaterials,
    setBuildings,
    setTruckingCalculations,
    setCustomImportVolumes,
    setCustomExcavationVolumes,
    setMaterialItems,
    setLaborItems,
    setEquipmentItems,
    setAdditionalCosts,
    setSettings,
    setLineItemMarkups
  } = setters;

  try {
    // Apply project info
    if (data.projectInfo) setProjectInfo(data.projectInfo);
    if (data.companyInfo) setCompanyInfo(data.companyInfo);

    // Apply calculator data
    if (data.calculators) {
      if (data.calculators.areaMaterials) setAreaMaterials(data.calculators.areaMaterials);
      if (data.calculators.bulkMaterials) setBulkMaterials(data.calculators.bulkMaterials);
      if (data.calculators.excavationMaterials) setExcavationMaterials(data.calculators.excavationMaterials);
      if (data.calculators.pipingMaterials) setPipingMaterials(data.calculators.pipingMaterials);
      if (data.calculators.tankMaterials) setTankMaterials(data.calculators.tankMaterials);
      if (data.calculators.buildings) setBuildings(data.calculators.buildings);
      if (data.calculators.truckingCalculations) setTruckingCalculations(data.calculators.truckingCalculations);
      if (data.calculators.customImportVolumes) setCustomImportVolumes(data.calculators.customImportVolumes);
      if (data.calculators.customExcavationVolumes) setCustomExcavationVolumes(data.calculators.customExcavationVolumes);
    }

    // Apply estimate data
    if (data.estimate) {
      if (data.estimate.materialItems) setMaterialItems(data.estimate.materialItems);
      if (data.estimate.laborItems) setLaborItems(data.estimate.laborItems);
      if (data.estimate.equipmentItems) setEquipmentItems(data.estimate.equipmentItems);
      if (data.estimate.additionalCosts) setAdditionalCosts(data.estimate.additionalCosts);
      if (data.estimate.settings) setSettings(data.estimate.settings);
      if (data.estimate.lineItemMarkups) setLineItemMarkups(data.estimate.lineItemMarkups);
    }

    // Apply custom entries to localStorage
    if (data.customEntries) {
      if (data.customEntries.materials) localStorage.setItem('customMaterialTypes', JSON.stringify(data.customEntries.materials));
      if (data.customEntries.bulkMaterials) localStorage.setItem('customBulkMaterials', JSON.stringify(data.customEntries.bulkMaterials));
      if (data.customEntries.excavationMaterials) localStorage.setItem('customExcavationMaterials', JSON.stringify(data.customEntries.excavationMaterials));
      if (data.customEntries.trucks) localStorage.setItem('customTruckTypes', JSON.stringify(data.customEntries.trucks));
      if (data.customEntries.pipes) localStorage.setItem('customPipeTypes', JSON.stringify(data.customEntries.pipes));
      if (data.customEntries.tanks) localStorage.setItem('customTankTypes', JSON.stringify(data.customEntries.tanks));
      if (data.customEntries.cyMaterials) localStorage.setItem('customCyToTonsMaterials', JSON.stringify(data.customEntries.cyMaterials));
      if (data.customEntries.materialTypesList) localStorage.setItem('estimateMaterialTypes', JSON.stringify(data.customEntries.materialTypesList));
      if (data.customEntries.laborTypesList) localStorage.setItem('estimateLaborTypes', JSON.stringify(data.customEntries.laborTypesList));
      if (data.customEntries.equipmentTypesList) localStorage.setItem('estimateEquipmentTypes', JSON.stringify(data.customEntries.equipmentTypesList));
    }

    return { success: true, message: 'Project data loaded successfully' };
  } catch (error) {
    console.error('Error applying project data:', error);
    return { success: false, message: 'Failed to apply project data' };
  }
};

/**
 * Clear all project data (new project)
 */
export const clearProjectData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.PROJECT_DATA);
    localStorage.removeItem(STORAGE_KEYS.LAST_SAVE);
    return { success: true, message: 'Project data cleared' };
  } catch (error) {
    return { success: false, message: 'Failed to clear project data' };
  }
};

/**
 * Get last save timestamp
 */
export const getLastSaveTime = () => {
  return localStorage.getItem(STORAGE_KEYS.LAST_SAVE);
};

/**
 * Auto-save project data (debounced)
 */
let autoSaveTimeout = null;
export const autoSaveProject = (projectData, delay = 5000) => {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  autoSaveTimeout = setTimeout(() => {
    saveProjectToStorage(projectData, projectData.projectInfo?.name);
  }, delay);
};
